import React from 'react';
import { useRecoilValue } from 'recoil';

import type * as api from '@gitbook/api-client';
import type { CommentContext } from '@gitbook/app-database';
import type { NavigationCommentsSideSheet } from '@gitbook/app-navigation';
import { type Document, serialize } from '@gitbook/slate';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { apiClient, getSerializedTableCellKey, isCommentNodeHoveredSelector } from '../../state';
import { useListPaginatedComments } from './useListPaginatedComments';

export type NavigateToCommentsFn = (input: {
    /** If defined, the current page should be changed to the given page id */
    pageId?: string;
    /** The state of the comments side sheet to navigate to */
    sideSheet: NavigationCommentsSideSheet | undefined;
    /** Whether to replace the current navigation state */
    replace?: boolean;
}) => void;

type CommentsNodeMap = {
    [nodeKey: string]: api.Comment[] | undefined;
};

type CommentsPageMapValue = {
    perNode: CommentsNodeMap;
    pageLevel: api.Comment[];
};

type CommentsPageMap = {
    [pageId: string]: CommentsPageMapValue;
};

type CommentsFetchedContextValue = {
    /** Fetched comments indexed by page id */
    perPage: CommentsPageMap;
};

type CommentsNavigationContextValue = {
    /** The space id being commented on */
    spaceId: string;
    /** The current change request id being commented on */
    changeRequestId: string | undefined;
    /** The current page id being commented on */
    currentPageId: string | null;
    /** The current sidesheet state being commented on */
    currentSideSheet: NavigationCommentsSideSheet | undefined;
    /** Function to navigate to a comments side sheet */
    navigateToComments: NavigateToCommentsFn;
};

type CommentsScopedContextValue = {
    pageId: string | null;
};

/** Store the fetched comments for a specific content */
const CommentsContext = React.createContext<CommentsFetchedContextValue | null>(null);

/** Store the state of navigation for comments */
const CommentsNavigationContext = React.createContext<CommentsNavigationContextValue | null>(null);

/** Store the currently scoped page id for comments */
const CommentsScopedPageIdContext = React.createContext<CommentsScopedContextValue>({
    pageId: null,
});

export type CommentNodeTarget = string | undefined;

export type CommentUISurface = 'none' | 'popover' | 'side-panel';
export type CommentUIIntent = 'idle' | 'create' | 'view';

export interface UseCommentsProps {
    /** The node (e.g. block) to read and write comments on */
    nodeKey: CommentNodeTarget;
    /** Table cell details if the comment is on a table cell */
    tableCell?: api.TableCellTarget;
}

export interface UseCommentsReturn {
    /** The comment context */
    commentContext: CommentContext;
    /** Comments for the node */
    nodeComments: api.Comment[];
    /** Whether there are comments on this node */
    hasComments: boolean;
    /** Counts for the number of comments on this node */
    commentCount: {
        total: number;
        topLevel: number;
    };
    /** The active comment */
    activeComment: api.Comment | null;
    /** High-level description of how comments should render for this node */
    commentUI: {
        surface: CommentUISurface;
        intent: CommentUIIntent;
        isTargeted: boolean;
    };
    /** Submit a new comment */
    submitComment: (body: Document) => Promise<void>;
    /** Open the comments UI (sidebar or popover) for this node */
    openNodeComments: (intent?: CommentUIIntent) => void;
    /** Open a provided comment thread */
    openCommentThread: (comment: api.Comment | null) => void;
    /** Close the popover */
    closePopover: () => void;
    /** State of the node related to comments UI */
    nodeState: {
        /** Is a comment linked to this node currently hovered */
        isHovered: boolean;
        /** Is this node also in the current navigation state */
        isOpened: boolean;
        /** Whether the add comment popover is open */
        addNewPopoverIsOpen: boolean;
        /** Whether the read comments popover is open */
        readPopoverIsOpen: boolean;
        /** Where the comments UI is being rendered */
        uiSurface: CommentUISurface;
        /** The current intent of the comments UI for this node */
        uiIntent: CommentUIIntent;
    };
}

/**
 * A context provider for comments on a document. Returns a stable ref to a map of node
 * keys to comments.
 */
export function FetchComments(props: {
    /** Whether to skip fetching comments */
    skip?: boolean;
    /** The space id being commented on */
    spaceId: string;
    /** The change request id being commented on */
    changeRequestId: string | undefined;
    /** The page id being commented on */
    pageId?: string; // TODO: Remove? so that it fetches the comments only once for a change request or space
    children: React.ReactNode;
}): React.ReactElement {
    const { skip = false, spaceId, pageId, changeRequestId, children } = props;

    const [comments] = useListPaginatedComments({
        spaceId,
        changeRequestId,
        pageId,
        status: 'open',
        skip,

        // We don't want to suspense here because we don't want to block the UI
        // when fetching comments.
        suspense: false,
    });

    // Index comments by page id and node id for efficient lookup.
    const cachedContextValue = React.useMemo(() => {
        const contextValue: CommentsFetchedContextValue = {
            perPage: {},
        };

        comments.forEach((comment) => {
            if (!comment.target.page) {
                return;
            }

            const pageMapValue = contextValue.perPage[comment.target.page.id] ?? {
                perNode: {},
                pageLevel: [],
            };
            contextValue.perPage[comment.target.page.id] = pageMapValue;

            if (!comment.target.node) {
                pageMapValue.pageLevel.push(comment);
                return;
            }

            const nodeMapValue = pageMapValue.perNode[comment.target.node.id] ?? [];
            pageMapValue.perNode[comment.target.node.id] = nodeMapValue;
            nodeMapValue.push(comment);
        });

        return contextValue;
    }, [comments]);

    return (
        <CommentsContext.Provider value={cachedContextValue}>{children}</CommentsContext.Provider>
    );
}

export type CommentsNavigationProviderProps =
    React.PropsWithChildren<CommentsNavigationContextValue>;

/**
 * Provider to indicate how to navigate comments.
 */
export function CommentsNavigationProvider(
    props: CommentsNavigationProviderProps
): React.ReactElement {
    const {
        spaceId,
        changeRequestId,
        currentPageId,
        currentSideSheet,
        navigateToComments,
        children,
    } = props;

    const stableNavigateToComments = useEventCallback(navigateToComments);

    const value = React.useMemo(() => {
        return {
            spaceId,
            changeRequestId,
            currentPageId,
            currentSideSheet,
            navigateToComments: stableNavigateToComments,
        };
    }, [spaceId, changeRequestId, currentPageId, currentSideSheet, stableNavigateToComments]);

    return (
        <CommentsNavigationContext.Provider value={value}>
            {children}
        </CommentsNavigationContext.Provider>
    );
}

/**
 * Provider to indicate the scoped page id for comments.
 * This is used when multiple pages are displayed in the same screen context, where the current page could be different from the page id being commented on.
 */
export function CommentsScopedProvider(
    props: React.PropsWithChildren<CommentsScopedContextValue>
): React.ReactElement {
    const { pageId, children } = props;
    const value = React.useMemo(() => ({ pageId }), [pageId]);
    return (
        <CommentsScopedPageIdContext.Provider value={value}>
            {children}
        </CommentsScopedPageIdContext.Provider>
    );
}

/**
 * Hook to provide comment state and handlers for any block or node.
 */
export function useComments({ nodeKey, tableCell }: UseCommentsProps): UseCommentsReturn {
    const commentsNavigationContext = React.useContext(CommentsNavigationContext);
    assertIsDefined(
        commentsNavigationContext,
        'useComments must be used within a CommentsNavigationProvider'
    );
    const { spaceId, changeRequestId, currentPageId, currentSideSheet, navigateToComments } =
        commentsNavigationContext;
    const scopeContext = React.useContext(CommentsScopedPageIdContext);
    const pageId = scopeContext.pageId ?? currentPageId;

    const commentsFetchedContext = React.useContext(CommentsContext);
    assertIsDefined(commentsFetchedContext, 'useComments must be used within a CommentsProvider');

    // Select all comments for the specific node in the current page
    const comments = React.useMemo(() => {
        if (!pageId) {
            return [];
        }

        const pageMapValue = commentsFetchedContext.perPage[pageId];
        if (!pageMapValue) {
            return [];
        }

        if (nodeKey === undefined) {
            return pageMapValue.pageLevel;
        }

        const blockComments = pageMapValue.perNode[nodeKey] ?? [];

        if (!tableCell?.record || !tableCell?.definition) {
            return blockComments;
        }

        return blockComments.filter(
            (comment) =>
                comment.target.tableCell &&
                comment.target.tableCell.record === tableCell.record &&
                comment.target.tableCell.definition === tableCell.definition
        );
    }, [commentsFetchedContext, pageId, nodeKey, tableCell?.record, tableCell?.definition]);

    // Get the active comment object from the sideSheet comment id, if any.
    const activeComment = React.useMemo(() => {
        return comments.find((comment) => comment.id === currentSideSheet?.comment) || null;
    }, [comments, currentSideSheet]);

    // Count all open top-level comments including their replies
    const numberOfComments = React.useMemo(
        () =>
            comments
                .filter((comment: api.Comment) => comment.status === 'open')
                .reduce((acc, curr) => acc + 1 + (curr.replies || 0), 0),
        [comments]
    );

    const commentCount = React.useMemo(
        () => ({
            total: numberOfComments,
            topLevel: comments.length,
        }),
        [comments.length, numberOfComments]
    );

    const hasComments = comments.length > 0;
    const urlParamContext = getUrlContextParam(currentSideSheet, hasComments);

    // Hover state: check whether a comment attached to this node is hovered
    const hoverKey = getSerializedTableCellKey(tableCell) || nodeKey;
    const hasCommentHovered = useRecoilValue(isCommentNodeHoveredSelector(hoverKey || ''));

    const commentUI = React.useMemo(
        () =>
            resolveNodeCommentUI({
                sideSheet: currentSideSheet,
                nodeKey,
                tableCell,
                hasComments,
                pageId,
                currentPageId,
            }),
        [currentSideSheet, nodeKey, tableCell, hasComments, pageId, currentPageId]
    );

    const addNewPopoverIsOpen = commentUI.surface === 'popover' && commentUI.intent === 'create';
    const readPopoverIsOpen = commentUI.surface === 'popover' && commentUI.intent === 'view';

    // Comment context for creating new comments
    const commentContext: CommentContext = React.useMemo(
        () => ({
            space: spaceId,
            changeRequest: changeRequestId ?? null,
            page: pageId ?? undefined,

            // If creating a comment for this node, include node context
            ...(nodeKey
                ? {
                      node: nodeKey,
                      ...(tableCell
                          ? {
                                tableCell: {
                                    record: tableCell.record,
                                    definition: tableCell.definition,
                                },
                            }
                          : {}),
                  }
                : {}),
        }),
        [spaceId, changeRequestId, pageId, nodeKey, tableCell]
    );

    const submitComment = async (body: Document) => {
        const data: api.PostCommentSchema = {
            // Comments are always either on a node (through a node id string) or they're on the page level (then `node` is undefined)
            node: nodeKey,
            page: commentContext.page,
            body: {
                document: serialize(body),
            },
            ...(commentContext.tableCell ? { tableCell: commentContext.tableCell } : {}),
        };

        const response = commentContext.changeRequest
            ? await apiClient.changeRequestComments.postCommentInChangeRequest(
                  commentContext.space,
                  commentContext.changeRequest,
                  data
              )
            : await apiClient.spaceComments.postCommentInSpace(commentContext.space, data);

        const comment = response.data;

        navigateToComments({
            pageId: commentContext.page ?? undefined,
            sideSheet: {
                type: 'comments',
                comment: comment.id,
                node: comment.target.node ? comment.target.node.id : undefined,
                context: urlParamContext,
                ...(commentContext.tableCell ? { tableCell: commentContext.tableCell } : {}),
            },
        });
    };

    const resolveNodeContext = React.useCallback(
        (comment: api.Comment | null) => {
            const commentNodeKey = comment?.target.node?.id ?? (!comment ? nodeKey : undefined);
            const tableCellTarget = comment?.target.tableCell ?? (!comment ? tableCell : undefined);

            return { nodeId: commentNodeKey, tableCellTarget };
        },
        [nodeKey, tableCell]
    );

    // Open the isolated details view of a specific comment thread
    const openCommentThread = React.useCallback(
        (comment: api.Comment | null) => {
            const { nodeId, tableCellTarget } = resolveNodeContext(comment);

            navigateToComments({
                pageId: comment?.target.page?.id ?? pageId ?? undefined,
                sideSheet: {
                    type: 'comments',
                    context: urlParamContext,
                    node: nodeId,
                    comment: comment?.id ?? undefined,
                    tableCell: tableCellTarget
                        ? {
                              record: tableCellTarget.record,
                              definition: tableCellTarget.definition,
                          }
                        : undefined,
                },
                replace: true,
            });
        },
        [navigateToComments, resolveNodeContext, urlParamContext, pageId]
    );

    // Navigate to the current node in the url navigation state. Accepts an `intent` to override the default automatic logic to decide on opening a panel vs popover based on states.
    const openNodeComments = React.useCallback(
        (intent?: CommentUIIntent) => {
            navigateToComments({
                pageId: pageId ?? undefined,
                sideSheet: {
                    type: 'comments',
                    node: nodeKey,
                    comment: undefined,
                    // By default, the urlParamContext determines whether to open a panel or a popover based on the presence of comments and whether the panel is already open. In some cases (like the comment UI for table cells), we pass an explicit `intent`.
                    context: intent === 'create' ? 'preview' : urlParamContext,
                    ...(tableCell
                        ? {
                              tableCell: {
                                  record: tableCell.record,
                                  definition: tableCell.definition,
                              },
                          }
                        : {}),
                },
            });
        },
        [navigateToComments, nodeKey, tableCell, urlParamContext, pageId]
    );

    /** Closes the popover by removing the comments from navigation state */
    const closePopover = () => {
        navigateToComments({
            sideSheet: undefined,
        });
    };

    return {
        hasComments,
        activeComment,
        commentCount,
        commentUI,
        commentContext,
        submitComment,
        openNodeComments,
        openCommentThread,
        closePopover,
        nodeComments: comments,
        nodeState: {
            isHovered: hasCommentHovered,
            isOpened: commentUI.isTargeted,
            readPopoverIsOpen,
            addNewPopoverIsOpen,
            uiSurface: commentUI.surface,
            uiIntent: commentUI.intent,
        },
    };
}

/**
 * Helper to decide whether the comment URL navigation state should open in a sidepanel or popover.
 * If the sideSheet is open (i.e. type === 'comments'), adopt the existing UI (sidepanel or popover).
 * Else, we always default to showing popovers.
 */
function getUrlContextParam(
    sideSheet: NavigationCommentsSideSheet | undefined,
    hasComments: boolean
): 'preview' | undefined {
    // We already have comments UI open (can be sidepanel (undefined context) or popover (context: 'preview')
    if (sideSheet && hasComments) {
        return sideSheet.context;
    } else {
        return 'preview';
    }
}

function resolveNodeCommentUI(args: {
    sideSheet: NavigationCommentsSideSheet | undefined;
    nodeKey: CommentNodeTarget;
    tableCell?: api.TableCellTarget;
    hasComments: boolean;
    pageId: string | null;
    currentPageId: string | null;
}): {
    surface: CommentUISurface;
    intent: CommentUIIntent;
    isTargeted: boolean;
    hasActiveComment: boolean;
} {
    const { sideSheet, nodeKey, tableCell, hasComments, pageId, currentPageId } = args;

    if (!sideSheet || currentPageId !== pageId) {
        return {
            surface: 'none',
            intent: 'idle',
            isTargeted: false,
            hasActiveComment: false,
        };
    }

    const surface: CommentUISurface = sideSheet.context === 'preview' ? 'popover' : 'side-panel';
    const isTargeted = matchesCommentNodeTarget(sideSheet, nodeKey, tableCell);
    const hasActiveComment = Boolean(sideSheet.comment);

    if (!isTargeted) {
        return {
            surface,
            intent: 'idle',
            isTargeted,
            hasActiveComment,
        };
    }

    const hasSomethingToShow = hasActiveComment || hasComments;
    const intent: CommentUIIntent =
        surface === 'popover'
            ? hasSomethingToShow
                ? 'view'
                : 'create'
            : hasSomethingToShow
              ? 'view'
              : 'idle';

    return {
        surface,
        intent,
        isTargeted,
        hasActiveComment,
    };
}

function matchesCommentNodeTarget(
    sideSheet: NavigationCommentsSideSheet,
    nodeKey: CommentNodeTarget,
    tableCell?: api.TableCellTarget
): boolean {
    if (sideSheet.type !== 'comments') {
        return false;
    }

    if (!matchesNodeKey(sideSheet.node, nodeKey)) {
        return false;
    }

    if (!tableCell && !sideSheet.tableCell) {
        return true;
    }

    if (!tableCell || !sideSheet.tableCell) {
        return false;
    }

    return (
        sideSheet.tableCell.record === tableCell.record &&
        sideSheet.tableCell.definition === tableCell.definition
    );
}

function matchesNodeKey(sideSheetNode: string | undefined, nodeKey: CommentNodeTarget): boolean {
    if (!nodeKey) {
        return !sideSheetNode;
    }

    return sideSheetNode === nodeKey;
}
